class SenaTheme {
  constructor() {
    this.bindQuantityButtons();
    this.bindProductGalleries();
    this.bindVariantSelectors();
    this.bindSizeGuides();
    this.bindAutoSubmit();
    this.bindProductRecommendations();
    this.bindDesktopNavHover();
    this.bindFilterDrawers();
    this.bindSortMenus();
    this.bindTestimonialSliders();
    this.bindFaqAccordions();
    this.syncHeaderHeight();

    document.addEventListener('shopify:section:load', (event) => {
      this.bindSortMenus(event.target);
      this.bindTestimonialSliders(event.target);
      this.bindFaqAccordions(event.target);
      this.bindProductGalleries(event.target);
      this.bindSizeGuides(document);
    });
  }

  bindQuantityButtons() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quantity-button]');
      if (!button) return;

      const wrapper = button.closest('[data-quantity]');
      const input = wrapper?.querySelector('input[type="number"]');
      if (!input) return;

      const step = Number(input.step || 1);
      const min = Number(input.min || 1);
      const current = Number(input.value || min);
      input.value = String(Math.max(min, current + (button.dataset.quantityButton === 'increase' ? step : -step)));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  bindProductGalleries(scope = document) {
    scope.querySelectorAll('[data-product-gallery]:not([data-gallery-initialized])').forEach((gallery) => {
      const panels = Array.from(gallery.querySelectorAll('[data-gallery-panel]'));
      const thumbnails = Array.from(gallery.querySelectorAll('[data-gallery-thumbnail]'));
      const currentLabel = gallery.querySelector('[data-gallery-current]');
      const previousButton = gallery.querySelector('[data-gallery-previous]');
      const nextButton = gallery.querySelector('[data-gallery-next]');
      if (panels.length === 0 || thumbnails.length === 0) return;

      gallery.dataset.galleryInitialized = 'true';

      const activate = (thumbnail, moveFocus = false) => {
        const mediaId = thumbnail.dataset.mediaTarget;
        const activeIndex = thumbnails.indexOf(thumbnail);

        panels.forEach((panel) => {
          const isActive = panel.dataset.mediaId === mediaId;
          panel.hidden = !isActive;
          panel.classList.toggle('is-active', isActive);
          panel.setAttribute('aria-hidden', String(!isActive));
          if (!isActive) panel.querySelectorAll('video').forEach((video) => video.pause());
        });

        thumbnails.forEach((item) => {
          const isActive = item === thumbnail;
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-pressed', String(isActive));
        });

        if (currentLabel) currentLabel.textContent = String(activeIndex + 1);
        if (moveFocus) thumbnail.focus();
      };

      thumbnails.forEach((thumbnail, index) => {
        thumbnail.addEventListener('click', () => activate(thumbnail));
        thumbnail.addEventListener('keydown', (event) => {
          let nextIndex;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % thumbnails.length;
          else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + thumbnails.length) % thumbnails.length;
          else if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = thumbnails.length - 1;
          else return;

          event.preventDefault();
          activate(thumbnails[nextIndex], true);
        });
      });

      previousButton?.addEventListener('click', () => {
        const activeIndex = thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-pressed') === 'true');
        activate(thumbnails[(activeIndex - 1 + thumbnails.length) % thumbnails.length]);
      });

      nextButton?.addEventListener('click', () => {
        const activeIndex = thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-pressed') === 'true');
        activate(thumbnails[(activeIndex + 1) % thumbnails.length]);
      });
    });
  }

  bindVariantSelectors() {
    document.querySelectorAll('[data-variant-selects]').forEach((selector) => {
      const json = selector.querySelector('[data-variants-json]');
      const formId = selector.dataset.productForm;
      const form = document.getElementById(formId);
      if (!json || !form) return;

      const variants = JSON.parse(json.textContent);
      const optionGroups = Array.from(selector.querySelectorAll('[data-option-group]'));
      const optionInputs = Array.from(selector.querySelectorAll('[data-option-input]'));
      const hiddenInput = form.querySelector('input[name="id"]');
      const addButton = form.querySelector('[data-add-to-cart]');
      const addText = form.querySelector('[data-add-to-cart-text]');
      const price = document.querySelector(`[data-product-price="${selector.dataset.section}"]`);
      const stock = document.querySelector('[data-product-stock]');
      const sku = document.querySelector('[data-product-sku]');
      if (!hiddenInput || !addButton || !addText || optionGroups.length === 0) return;

      const update = (syncUrl = true) => {
        const selectedOptions = optionGroups.map((group) => group.querySelector('[data-option-input]:checked')?.value || '');
        const variant = variants.find((item) => item.options.every((option, index) => option === selectedOptions[index]));

        optionGroups.forEach((group, optionIndex) => {
          const selectedInput = group.querySelector('[data-option-input]:checked');
          const selectedLabel = group.querySelector('[data-option-selected]');
          if (selectedInput && selectedLabel) selectedLabel.textContent = selectedInput.value;

          group.querySelectorAll('[data-option-input]').forEach((input) => {
            input.disabled = !variants.some((item) => (
              item.available
              && item.options[optionIndex] === input.value
              && item.options.every((option, index) => index === optionIndex || option === selectedOptions[index])
            ));
          });
        });

        if (!variant) {
          addButton.disabled = true;
          addText.textContent = 'Unavailable';
          if (stock) {
            stock.textContent = 'Unavailable';
            stock.classList.add('product-stock--out');
          }
          return;
        }

        hiddenInput.value = variant.id;
        addButton.disabled = !variant.available;
        addText.textContent = variant.available ? 'Add to cart' : 'Sold out';
        if (stock) {
          stock.textContent = variant.available ? 'In stock' : 'Out of stock';
          stock.classList.toggle('product-stock--out', !variant.available);
        }
        if (sku) sku.textContent = variant.sku ? `SKU ${variant.sku}` : '';

        if (price) {
          const regular = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
            style: 'currency',
            currency: price.dataset.currency || 'USD'
          }).format(variant.price / 100);
          const compare = variant.compare_at_price && variant.compare_at_price > variant.price
            ? `<s class="price__compare">${new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: price.dataset.currency || 'USD' }).format(variant.compare_at_price / 100)}</s>`
            : '';
          price.innerHTML = `<span>${regular}</span>${compare}`;
        }

        if (syncUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }
      };

      optionInputs.forEach((input) => input.addEventListener('change', () => update()));
      update(false);
    });
  }

  bindSizeGuides(scope = document) {
    scope.querySelectorAll('[data-size-guide-modal]:not([data-size-guide-initialized])').forEach((modal) => {
      const dialog = modal.querySelector('[role="dialog"]');
      const closeButton = modal.querySelector('.size-guide-modal__close');
      const triggers = Array.from(document.querySelectorAll(`[data-size-guide-open][aria-controls="${modal.id}"]`));
      const closeTargets = Array.from(modal.querySelectorAll('[data-size-guide-close]'));
      const unitButtons = Array.from(modal.querySelectorAll('[data-size-guide-unit]'));
      const unitLabels = Array.from(modal.querySelectorAll('[data-size-guide-unit-label]'));
      const values = Array.from(modal.querySelectorAll('[data-size-guide-value]'));
      if (!dialog || triggers.length === 0) return;

      modal.dataset.sizeGuideInitialized = 'true';
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let lastTrigger;
      let closeTimer;

      const focusableElements = () => Array.from(dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((element) => element.getClientRects().length > 0);

      const setUnit = (unit) => {
        const useMetric = unit === 'cm';

        values.forEach((cell) => {
          const inches = Number(cell.dataset.inches);
          if (!Number.isFinite(inches)) return;
          cell.textContent = useMetric ? (inches * 2.54).toFixed(1) : cell.dataset.inches;
        });
        unitLabels.forEach((label) => { label.textContent = unit; });
        unitButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.sizeGuideUnit === unit)));
      };

      const openGuide = (trigger) => {
        window.clearTimeout(closeTimer);
        lastTrigger = trigger;
        modal.hidden = false;
        document.documentElement.classList.add('size-guide-is-open');
        triggers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
        window.requestAnimationFrame(() => {
          modal.classList.add('is-open');
          dialog.focus({ preventScroll: true });
        });
      };

      const finishClose = () => {
        modal.hidden = true;
        if (!document.querySelector('[data-size-guide-modal].is-open')) {
          document.documentElement.classList.remove('size-guide-is-open');
        }
        lastTrigger?.focus({ preventScroll: true });
      };

      const closeGuide = () => {
        if (modal.hidden) return;
        modal.classList.remove('is-open');
        triggers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
        window.clearTimeout(closeTimer);
        if (reducedMotion.matches) finishClose();
        else closeTimer = window.setTimeout(finishClose, 190);
      };

      triggers.forEach((trigger) => trigger.addEventListener('click', () => openGuide(trigger)));
      closeTargets.forEach((target) => target.addEventListener('click', closeGuide));
      unitButtons.forEach((button) => button.addEventListener('click', () => setUnit(button.dataset.sizeGuideUnit)));

      modal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeGuide();
          return;
        }

        if (event.key !== 'Tab') return;
        const focusable = focusableElements();
        if (focusable.length === 0) {
          event.preventDefault();
          dialog.focus();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });

      setUnit('in');
    });
  }

  bindAutoSubmit() {
    document.querySelectorAll('[data-auto-submit]').forEach((element) => {
      element.addEventListener('change', () => element.form?.submit());
    });
  }

  bindProductRecommendations() {
    document.querySelectorAll('[data-recommendations-url]').forEach(async (container) => {
      try {
        const response = await fetch(container.dataset.recommendationsUrl);
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const content = parsed.querySelector('[data-recommendations-content]');
        if (content?.innerHTML.trim()) container.innerHTML = content.innerHTML;
      } catch (error) {
        console.warn('Product recommendations could not be loaded.', error);
      }
    });
  }

  bindDesktopNavHover() {
    document.querySelectorAll('.desktop-nav__dropdown').forEach((dropdown) => {
      const summary = dropdown.querySelector(':scope > summary');
      if (!summary) return;

      dropdown.querySelectorAll('.collection-tree').forEach((tree) => {
        tree.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') tree.classList.add('is-expanded');
        });

        tree.addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'mouse') tree.classList.remove('is-expanded');
        });
      });

      dropdown.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;

        dropdown.setAttribute('open', '');
      });

      summary.addEventListener('click', (event) => {
        const isMouseClick = event.detail > 0 && (!event.pointerType || event.pointerType === 'mouse');
        if (!isMouseClick) return;

        event.preventDefault();
        dropdown.setAttribute('open', '');
      });

      dropdown.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        if (dropdown.contains(document.activeElement)) return;

        dropdown.removeAttribute('open');
      });
    });
  }

  bindFilterDrawers() {
    document.querySelectorAll('[data-filter-drawer]').forEach((drawer) => {
      const trigger = drawer.querySelector(':scope > summary');
      const closeButton = drawer.querySelector('[data-filter-close]');

      const closeDrawer = () => {
        drawer.removeAttribute('open');
        trigger?.focus();
      };

      closeButton?.addEventListener('click', closeDrawer);
      drawer.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        closeDrawer();
      });
    });
  }

  bindSortMenus(scope = document) {
    scope.querySelectorAll('[data-sort-menu]:not([data-sort-menu-initialized])').forEach((menu) => {
      menu.dataset.sortMenuInitialized = 'true';

      menu.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        menu.removeAttribute('open');
        menu.querySelector(':scope > summary')?.focus();
      });

      document.addEventListener('click', (event) => {
        if (!menu.contains(event.target)) menu.removeAttribute('open');
      });
    });
  }

  bindTestimonialSliders(scope = document) {
    scope.querySelectorAll('[data-testimonials-slider]:not([data-slider-initialized])').forEach((slider) => {
      const viewport = slider.querySelector('[data-slider-viewport]');
      const slides = Array.from(slider.querySelectorAll('[data-testimonial-slide]'));
      const previousButton = slider.querySelector('[data-slider-previous]');
      const nextButton = slider.querySelector('[data-slider-next]');
      const currentLabel = slider.querySelector('[data-slider-current]');
      const totalLabel = slider.querySelector('[data-slider-total]');

      if (!viewport || slides.length === 0) return;

      slider.dataset.sliderInitialized = 'true';
      if (totalLabel) totalLabel.textContent = String(slides.length).padStart(2, '0');

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const autoplaySpeed = Math.max(3000, Number(slider.dataset.autoplaySpeed) || 5000);
      let activeIndex = 0;
      let autoplayTimer;
      let scrollTicking = false;

      const getSlideOffset = (index) => slides[index].offsetLeft - slides[0].offsetLeft;
      const getLastStartIndex = () => {
        const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        return slides.reduce((lastIndex, slide, index) => (
          getSlideOffset(index) <= maxScrollLeft + 1 ? index : lastIndex
        ), 0);
      };

      const updateCounter = () => {
        activeIndex = slides.reduce((closestIndex, slide, index) => {
          const currentDistance = Math.abs(getSlideOffset(closestIndex) - viewport.scrollLeft);
          const nextDistance = Math.abs(getSlideOffset(index) - viewport.scrollLeft);
          return nextDistance < currentDistance ? index : closestIndex;
        }, 0);

        if (currentLabel) currentLabel.textContent = String(activeIndex + 1).padStart(2, '0');
        scrollTicking = false;
      };

      const goToSlide = (index) => {
        const lastStartIndex = getLastStartIndex();
        if (index > lastStartIndex) activeIndex = 0;
        else if (index < 0) activeIndex = lastStartIndex;
        else activeIndex = index;

        viewport.scrollTo({
          left: getSlideOffset(activeIndex),
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      };

      const stopAutoplay = () => window.clearInterval(autoplayTimer);
      const startAutoplay = () => {
        stopAutoplay();
        if (slider.dataset.autoplay !== 'true' || reducedMotion.matches || slides.length < 2) return;
        autoplayTimer = window.setInterval(() => goToSlide(activeIndex + 1), autoplaySpeed);
      };

      previousButton?.addEventListener('click', () => {
        goToSlide(activeIndex - 1);
        startAutoplay();
      });

      nextButton?.addEventListener('click', () => {
        goToSlide(activeIndex + 1);
        startAutoplay();
      });

      viewport.addEventListener('scroll', () => {
        if (scrollTicking) return;
        scrollTicking = true;
        window.requestAnimationFrame(updateCounter);
      }, { passive: true });

      slider.addEventListener('pointerenter', stopAutoplay);
      slider.addEventListener('pointerleave', startAutoplay);
      slider.addEventListener('focusin', stopAutoplay);
      slider.addEventListener('focusout', (event) => {
        if (!slider.contains(event.relatedTarget)) startAutoplay();
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stopAutoplay();
        else startAutoplay();
      });
      reducedMotion.addEventListener?.('change', startAutoplay);
      window.addEventListener('resize', updateCounter, { passive: true });

      updateCounter();
      startAutoplay();
    });
  }

  bindFaqAccordions(scope = document) {
    scope.querySelectorAll('.faq-item:not([data-faq-initialized]), .product-details details:not([data-faq-initialized])').forEach((item) => {
      const summary = item.querySelector(':scope > summary');
      const answer = item.querySelector(':scope > .faq-item__answer, :scope > .product-details__content');
      if (!summary || !answer) return;

      item.dataset.faqInitialized = 'true';
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let animation;

      const finish = (open) => {
        item.open = open;
        item.classList.remove('is-closing');
        item.style.removeProperty('height');
        item.style.removeProperty('overflow');
        animation = null;
      };

      const toggle = (open) => {
        if (reducedMotion.matches || typeof item.animate !== 'function') {
          animation?.cancel();
          finish(open);
          return;
        }

        const startHeight = item.getBoundingClientRect().height;
        animation?.cancel();
        item.style.height = `${startHeight}px`;
        item.style.overflow = 'hidden';
        item.open = true;
        item.classList.toggle('is-closing', !open);

        const borderHeight = item.offsetHeight - item.clientHeight;
        const endHeight = open
          ? item.scrollHeight + borderHeight
          : summary.offsetHeight + borderHeight;

        animation = item.animate(
          { height: [`${startHeight}px`, `${endHeight}px`] },
          { duration: 320, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
        );
        animation.onfinish = () => finish(open);
      };

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        toggle(item.classList.contains('is-closing') || !item.open);
      });
    });
  }

  syncHeaderHeight() {
    let ticking = false;
    const update = () => {
      const header = document.querySelector('.site-header');
      const announcement = document.querySelector('.announcement');
      const headerBottom = header?.getBoundingClientRect().bottom || 0;
      const announcementBottom = Math.max(0, announcement?.getBoundingClientRect().bottom || 0);
      document.documentElement.style.setProperty('--header-bottom', `${Math.max(headerBottom, announcementBottom)}px`);
      ticking = false;
    };
    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('resize', requestUpdate, { passive: true });
    window.addEventListener('scroll', requestUpdate, { passive: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new SenaTheme());
} else {
  new SenaTheme();
}
